/**
 * CouponWidgetView - Presentation Component
 *
 * Renders the coupon widget with horizontal scroll on mobile, grid on desktop.
 *
 * @see mobile: module-flash-sale/sales-rule/components/coupon-widget-content/
 */

'use client';

import React, { useCallback, useMemo } from 'react';
import {
    cn,
    Text,
    Icon,
    VoucherCard,
    type VoucherCardData,
    AxisEnum,
    ScrollView,
} from '@kidslabs/mario-nextjs-react-ui-core';
import type { CouponContentResponse } from '@kidslabs/module-flash-sale';
import styles from './coupon-widget.module.css';
import { getCouponImageUri } from '@kidslabs/module-core';

interface CouponItemData {
    coupon: CouponContentResponse & { imageUri?: string };
    actionLabel: string;
    isAdding: boolean;
    isInWallet: boolean;
}

interface CouponWidgetViewProps {
    title: string;
    coupons?: CouponItemData[];
    variant?: 'voucher' | 'eWalletVoucher';
    isLoading?: boolean;
    onAction?: (coupon: CouponContentResponse) => void;
    onShowCondition?: (coupon: CouponContentResponse) => void;
    onViewAll?: () => void;
    className?: string;
    isMobile?: boolean;
}

export const CouponWidgetView: React.FC<CouponWidgetViewProps> = React.memo(
    ({
        title,
        coupons = [],
        isLoading = false,
        onAction,
        onViewAll,
        className,
        isMobile,
        variant = 'voucher',
    }: CouponWidgetViewProps) => {
        const containerClassName = useMemo(
            () => cn(styles.container, className, isMobile ? styles.containerMobile : styles.containerDesktop),
            [className, isMobile]
        );

        const titleClassName = useMemo(
            () => cn(styles.title, isMobile ? styles.titleMobile : styles.titleDesktop),
            [isMobile]
        );

        const voucherCardSize = useMemo(() => (isMobile ? 'small' : 'large'), [isMobile]);

        const handleVoucherAction = useCallback(
            (coupon: CouponContentResponse) => {
                onAction?.(coupon);
            },
            [onAction]
        );

        const renderSkeleton = useCallback(
            () => (
                <div className={containerClassName}>
                    <div className={styles.header}>
                        <div className={styles.skeletonTitle} />
                    </div>
                    <div className={styles.scrollContainer}>
                        {Array.from({ length: 4 }).map((_, index) => (
                            <div key={`skeleton-${index}`} className={styles.skeletonCard} />
                        ))}
                    </div>
                </div>
            ),
            [containerClassName]
        );

        const mappedCoupons = useMemo(
            () =>
                coupons.map(({ coupon: item, actionLabel, isAdding, isInWallet }) => {
                    let imagePartner;

                    if (variant === 'voucher') {
                        imagePartner = item.image ? [{ image: item.image }] : undefined;
                    } else {
                        imagePartner = item.partner;
                    }

                    const voucherData: VoucherCardData = {
                        ...(item as VoucherCardData),
                        imageUri: getCouponImageUri({
                            partner: imagePartner,
                            brand_image: item.brand_image,
                        }),
                    };

                    return {
                        item,
                        actionLabel,
                        isAdding,
                        isInWallet,
                        voucherData,
                    };
                }),
            [coupons, variant]
        );

        if (isLoading) {
            return renderSkeleton();
        }

        return (
            <div className={containerClassName}>
                {/* Header */}
                <div className={styles.header}>
                    <Text variant="h2" size="large" weight="bold" className={titleClassName}>
                        {title}
                    </Text>
                    {onViewAll && (
                        <button
                            type="button"
                            className={styles.viewAllButton}
                            onClick={onViewAll}
                            aria-label="Xem tất cả coupon"
                        >
                            <Text size="medium" weight="medium" className={styles.viewAllText}>
                                Xem tất cả
                            </Text>
                            <Icon name="chevronRight" size={16} className={styles.viewAllIcon} />
                        </button>
                    )}
                </div>

                {/* Coupon cards - horizontal scroll on mobile, grid on desktop */}
                <div className={styles.scrollViewGroup}>
                    <ScrollView isVisible={false} axis={AxisEnum.X}>
                        <ul className={styles.scrollViewWrapper} aria-label={title}>
                            {mappedCoupons.map(({ item, actionLabel, isAdding, isInWallet, voucherData }, index) => {
                                const handleVoucher = () => {
                                    handleVoucherAction(item);
                                };

                                return (
                                    <li
                                        key={`${item.salesrule?.coupon_id ?? item.salesrule?.code ?? item.name ?? 'unknown'}-${index}`}
                                        className={styles.cardWrapper}
                                    >
                                        <VoucherCard
                                            voucher={voucherData}
                                            status={isInWallet}
                                            labelButton={actionLabel}
                                            textExpiry="HSD"
                                            handleVoucher={handleVoucher}
                                            isAdding={isAdding}
                                            size={voucherCardSize}
                                        />
                                    </li>
                                );
                            })}
                        </ul>
                    </ScrollView>
                </div>
            </div>
        );
    }
);

CouponWidgetView.displayName = 'CouponWidgetView';
